import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ExTabs, useResizeObserver, useToastMessage } from "@energyx-dev/energyx-ui";
import { Corporation } from "components/signup/Corporation";
import { Normal } from "components/signup/Normal";
import { hackleClient } from "hackle/hackleInstance";
import useTranslation from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { convertPath } from "utils/utils";

export const SignUp = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const isEnglishPage = useTranslation();
  const urlParameter = window.document.referrer;
  const searchParams = new URLSearchParams(location.search);
  const callbackUrl = searchParams.get("callbackUrl");
  const device = useResizeObserver();
  const { toastContainer } = useToastMessage();
  const signUpCompletedRedirect = () => {
    hackleClient.track({
      key: "complete_Join",
      properties: {
        SiteName: urlParameter || process.env.ENERGYX_URL,
        User_Type: tabIndex === 0 ? "일반" : "법인",
        ...(callbackUrl && { From: callbackUrl }),
      },
    });
    if (checkCallbackUrlIsEnergyxDomain()) {
      return (window.location.href = `${process.env.API_URL}/sso/login?redirectUrl=${callbackUrl}&continueUrl=${window.location.origin}`);
    } else {
      window.location.href = `${process.env.API_URL}/sso/login?redirectUrl=${
        urlParameter || process.env.ENERGYX_URL
      }&continueUrl=${window.location.origin}`;
    }
  };

  const checkCallbackUrlIsEnergyxDomain = () => {
    if (!callbackUrl) return false;

    const energyxDomainArr = [
      process.env.ENERGYX_URL,
      process.env.STORE_URL,
      process.env.TOMARCH_URL,
      process.env.DESIGN_WHOS_URL,
    ];

    return energyxDomainArr.some((domain) => callbackUrl.includes(domain ?? ""));
  };

  useEffect(() => {
    hackleClient.track({
      key: "enter_Join",
      properties: {
        SiteName: urlParameter ? convertPath(urlParameter as string) : "",
      },
    });
  }, []);
  return (
    <>
      {device && toastContainer(device)}
      <SignUpContainer>
        <Header>{isEnglishPage ? "Join us" : "회원가입"}</Header>

        <Tabs>
          <div
            css={(theme) => css`
              border-bottom: 1px solid ${theme.color.mono[10]};
            `}
          >
            <ExTabs
              tabs={[
                {
                  name: isEnglishPage ? "Individual" : "일반회원가입",
                  isExistNew: false,
                  className: "signup-normal",
                },
                {
                  name: isEnglishPage ? "Business" : "법인회원가입",
                  isExistNew: false,
                  className: "signup-corporation",
                },
              ]}
              variant="underline"
              size="small"
              contentsIndex={tabIndex}
              setContentsIndex={setTabIndex}
              gap={0}
            />
          </div>
          {tabIndex === 0 && (
            <Notice>
              <span className="xicon-">info_fill</span>
              <span>
                {isEnglishPage
                  ? "A sole proprietor must create a business account."
                  : "개인사업자의 경우, 법인회원으로 가입하셔야 합니다."}
              </span>
            </Notice>
          )}
          {tabIndex === 0 ? (
            <Normal signUpCompletedRedirect={signUpCompletedRedirect} />
          ) : (
            <Corporation signUpCompletedRedirect={signUpCompletedRedirect} />
          )}
        </Tabs>
      </SignUpContainer>
    </>
  );
};

const SignUpContainer = styled.section`
  width: 100%;
  margin: 0 auto 0;
  display: flex;
  flex-direction: column;
  padding: 80px 0;
  align-items: center;
  gap: 40px;

  ${(props) => props.theme.breakpoints.down("DESKTOP")} {
    width: 448px;
    padding: 24px 0 40px;
    gap: 24px;
  }

  ${(props) => props.theme.breakpoints.down("TABLET")} {
    width: 100%;
    padding: 24px 16px 40px 16px;
  }
`;

const Header = styled.h1`
  ${({ theme }) => theme.font.H2};
  color: ${({ theme }) => theme.color.black};

  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    ${({ theme }) => theme.font.Body3_h}
  }
`;

const Tabs = styled.article`
  display: flex;
  flex-direction: column;
  width: 448px;
  gap: 24px;

  > div:first-of-type {
    display: flex;
    flex-direction: column;
  }

  ${(props) => props.theme.breakpoints.down("DESKTOP")} {
    width: 100%;
  }
`;

const Notice = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 8px;
  color: ${(props) => props.theme.color.mono[70]};
  background-color: ${(props) => props.theme.color.primary[5]};
  border-radius: 8px;

  span:first-of-type {
    font-size: 20px;
    line-height: 20px;
  }

  span:last-of-type {
    ${(props) => props.theme.font.Caption}
  }
`;
